<template>
    <div id="workflow-form">
        <div class="row">
            <div class="input title-input">
                <div class="label">{{ $t("task.title") }}</div>
                <a-input class="classic-new-input" v-model:value="form.title" :placeholder="$t('task.taskNumber')" />
            </div>
        </div>
        <div class="row">
            <div class="input title-input">
                <div class="label">{{ $t("message.description") }}</div>
                <quill-editor
                    class="quill-editor"
                    :options="editorOption"
                    v-model="form.description"
                    :placeholder="$t('task.workflowCreateDesc')"
                />
            </div>
        </div>
        <div class="row">
            <div class="input title-input">
                <div class="label">{{ $t("task.stagesAndStatuses") }}</div>
                <draggable
                    :options="dragOptions"
                    v-model="form.pipelines"
                    draggable=".drag-row"
                    :group="{ name: 'pipeline', pull: 'clone', put: false }"
                >
                    <transition-group class="drag-rows">
                        <div
                            class="drag-row"
                            v-for="(pipeline, index) of form.pipelines"
                            :style="{ '--row-color': `${pipeline.color}` }"
                            :key="`p_` + index"
                            @dblclick="editStage(pipeline)"
                        >
                            <div class="pipeline-content">
                                <div class="pipeline-picker">
                                    <input type="color" style="opacity: 0" v-model="pipeline.color" />
                                </div>
                                <div
                                    class="pipeline-name"
                                    :class="{ editable: pipeline.isEditing }"
                                    contenteditable="true"
                                    @blur="(e) => (pipeline.name = e.target.innerText)"
                                >
                                    {{ pipeline.name }}
                                </div>
                                <select v-if="pipeline.isEditing" v-model="pipeline.type">
                                    <option value="todo">{{ $t("auto.todo") }}</option>
                                    <option value="in-progress">{{ $t("auto.inProgress") }}</option>
                                    <option value="approval">{{ $t("auto.approval") }}</option>
                                    <option
                                        value="completed"
                                        v-if="
                                            form.pipelines.filter((p) => p.type === 'completed').length < 1 ||
                                            pipeline.type === 'completed'
                                        "
                                    >
                                        {{ $t("auto.completed") }}
                                    </option>
                                </select>
                            </div>
                            <div class="last-action">
                                <div class="pipeline-type">
                                    <span>{{ formatType(pipeline.type) }}</span>
                                </div>
                                <div class="pipeline-actions">
                                    <div
                                        class="material-icons edit-button"
                                        v-if="!pipeline.isEditing"
                                        @click="editStage(pipeline)"
                                    >
                                        edit
                                    </div>
                                    <div
                                        class="material-icons save-button"
                                        v-if="pipeline.isEditing"
                                        @click="saveStage(pipeline)"
                                    >
                                        save
                                    </div>
                                    <div
                                        class="material-icons delete-button"
                                        v-if="canDelete(pipeline.type)"
                                        @click="deleteStage(pipeline)"
                                    >
                                        delete
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition-group>
                </draggable>
                <SupsisButton
                    style="margin-top: 10px"
                    @click="$feature.beforeCreateTaskColumn(createStage, form.pipelines.length)"
                >{{ $t("task.createStage") }}</SupsisButton
                >
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import Draggable from "vuedraggable";
import SupsisButton from "../common/SupsisButtonOld.vue";
import Modal from "../common/Modal2.vue";

export default {
    name: "WorkflowForm",
    components: {
        Draggable,
        SupsisButton,
        Modal,
    },
    props: {
        details: {
            workflowId: null,
        },
        editMode: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        form: {
            title: null,
            description: " ",
            pipelines: [
                {
                    color: "#7FFFFF",
                    name: "Todo",
                    type: "todo",
                },
                {
                    color: "#f9c851",
                    name: "In Progress",
                    type: "in-progress",
                },
                {
                    color: "#f9724d",
                    name: "Approval",
                    type: "approval",
                },
                {
                    color: "#16cfae",
                    name: "Completed",
                    type: "completed",
                },
            ],
        },
        dragOptions: {
            group: "pipeline",
            animation: 200,
        },
        editorOption: {
            theme: "snow",
            modules: {
                toolbar: [
                    ["bold", "italic", "underline", "strike"],
                    [{ header: [1, 2, 3, 4, 5, 6, false] }],
                    [{ color: [] }, { background: [] }],
                    [{ align: [] }],
                    [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
                    ["link"],
                ],
            },
        },
    }),
    computed: {
        ...mapState({
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc.push({
                        value: user._id,
                        label: user.name,
                    });
                    return acc;
                }, []),
            defaultWorkflow: (state) => state.tasks.defaultWorkflow,
        }),
        canDelete() {
            return (type) => {
                return this.form.pipelines.filter((pipeline) => pipeline.type === type).length > 1;
            };
        },
    },
    methods: {
        ...mapActions({
            deletePipelineStage: "tasks/deletePipelineStage",
            addNewWindow: "app/addNewWindow",
            getWorkflowDetails: "tasks/getWorkflowDetails",
        }),
        formatType(type) {
            return type[0].toUpperCase() + type.slice(1);
        },
        createStage() {
            const completedExists = this.form.pipelines.some((pipeline) => pipeline.type === "completed");
            if (!completedExists) {
                this.form.pipelines = [
                    ...this.form.pipelines,
                    {
                        isDeleted: true,
                        color: "#16cfae",
                        name: "Completed",
                        type: "completed",
                    },
                ];
            }
            this.form.pipelines = [
                ...this.form.pipelines,
                {
                    isDeleted: false,
                    color: "#f9724d",
                    name: "Approval",
                    type: "approval",
                },
            ];
        },
        deleteStage(pipeline) {
            if (this.editMode) {
                if (pipeline.taskCount === 0) {
                    this.deletePipelineStage({
                        pipelineId: pipeline._id,
                        workflowId: this.details?.workflowId,
                    });
                    this.form.pipelines = this.form.pipelines.filter((p) => p !== pipeline);
                } else if (pipeline.taskCount > 0) {
                    this.$notification.error({
                        message: "Hata",
                        description: "İlgili Bölümdeki Kartlarınızı Silmeden Veya Taşımadan Bölümü Silemezsiniz",
                    });
                }
            } else {
                this.form.pipelines = this.form.pipelines.filter((p) => p !== pipeline);
            }
        },
        editStage(pipeline) {
            this.$set(pipeline, "isEditing", !pipeline.isEditing);
        },
        saveStage(pipeline) {
            this.$set(pipeline, "isEditing", false);
        },
    },
    mounted() {
        this.getWorkflowDetails({
            workflowId: this.details?.workflowId,
        }).then((data) => {
            this.form = {
                ...data,
                pipelines: data.pipeline,
            };
        });
    },
    watch: {
        form: {
            deep: true,
            handler() {
                this.$emit("update-form", this.form);
            },
        },
    },
};
</script>
<style lang="scss">
#workflow-form {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .pipeline-name.editable {
        border: 1px dashed #ec4b1d;
        background-color: #fff3f0;
    }

    .pipeline-type input {
        border: 1px solid #ec4b1d;
        background-color: #fff3f0;
        padding: 2px;
    }

    .pipeline-actions .material-icons {
        font-size: 20px;
        width: 30px;
        height: 30px;
        border-radius: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }

    .edit-button {
        color: #3c3c3c;
        background-color: #f1f1f1;
        border: 1px solid #ec4b1d;

        &:hover {
            background-color: #ec4b1d;
            color: white;
        }
    }

    .save-button {
        color: #3c3c3c;
        background-color: #d4edda;
        border: 1px solid #28a745;
        &:hover {
            background-color: #28a745;
            color: white;
        }
    }

    .delete-button {
        color: #3c3c3c;
        background-color: #f8d7da;
        border: 1px solid #dc3545;

        &:hover {
            background-color: #dc3545;
            color: white;
        }
    }

    .pipeline-content select {
        margin-left: 10px;
        padding: 2px;
        border: 1px solid #ec4b1d;
        background-color: #fff3f0;
    }

    .pipeline-name {
        direction: ltr;
        text-align: left;
        min-width: 25px;
    }

    .classic-new-input {
        padding: 20px;
        background: #f1f1f1;
        border: none;
        color: #3c3c3c;
        font-weight: bold;
        font-size: 14px;
        &:focus {
            border: #ec4a1d65 !important;
            box-shadow: 0 0 0 2px #ffaa0037;
        }
        &::placeholder {
            opacity: 1;
            font-weight: bold;
        }
    }

    .input {
        font-weight: bold;
        .label {
            font-size: 16px;
            margin-bottom: 4px;
        }
        &.title-input {
            width: 100%;
        }
    }
    .quill-editor {
        max-height: 20%;
        width: 100%;
        .ql-toolbar.ql-snow {
            background: #f1f1f1;
            border-radius: 4px 4px 0px 0px;
            border: none;
        }
        .ql-container.ql-snow {
            height: 200px;
            max-height: 200px;
            min-height: 200px;
            background: #f1f1f1;
            border-radius: 0px 0px 4px 4px;
            overflow: hidden;
            border: none !important;
        }
    }
    .drag-rows {
        display: flex;
        flex-direction: column;
        .drag-row {
            cursor: move;
        }
    }
    .drag-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        position: relative;
        border-left: 5px solid var(--row-color);
        border-bottom: 1px solid var(--row-color);
        height: 40px;
        padding: 0px 15px;
        background: white;
        .pipeline-content {
            display: flex;
            align-items: center;
            gap: 15px;
        }
        .pipeline-picker {
            width: 25px;
            height: 25px;
            border-radius: 4px;
            background: var(--row-color);
            overflow: hidden;
        }
        .pipeline-actions {
            display: none;
            align-items: center;
            gap: 5px;
            cursor: pointer;
            .material-icons {
                font-size: 20px;
                width: 30px;
                height: 30px;
                border-radius: 30px;
                color: rgb(197, 197, 197);
                display: flex;
                align-items: center;
                justify-content: center;
                &:hover {
                    background-color: #f9724d42;
                    color: #ec4b1d;
                }
            }
        }
        &:hover {
            background-color: #f1f1f1;
            .pipeline-actions {
                display: flex;
            }
            .pipeline-type {
                display: none;
            }
        }
        .pipeline-type {
            background: var(--row-color);
            padding: 3px 6px;
            border-radius: 4px;
            color: white;
        }
    }
    .pipeline-select {
        .ant-select-selection {
            background-color: var(--row-color) !important;
            color: white !important;
        }
    }
}
</style>
